<template>
	<form class="key-result-form" :data-number="number">
		<header>
			<h1 class="key-result-form__title">{{ $t( "common-gokr.components.NewKeyResultForm.title", { number, } ) }}</h1>
			<FontAwesomeIcon
				class="key-result-form__discard"
				:icon="[ `fas`, `times` ]"
				@click="onDiscardClicked"
			/>
		</header>
		<main class="key-result-form__main">
			<div>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="disabled || disabledFields.name"
					:focus="isFocused( `name` )"
					:is-valid="!errors.name"
					:selection="focusedSelectionFor( `name` )"
					:validation-error="errorFor( `name` )"
					:value="name"
					:label="$t( `common-gokr.components.NewKeyResultForm.nameFieldLabel` )"
					name="name"
					:placeholder="$t( `common-gokr.components.NewKeyResultForm.nameFieldPlaceholder` )"
					@input="onNameInput"
					@click="onNameFocus"
				/>
			</div>

			<div>
				<LabeledConfidenceLevelDropDown
					:disabled="disabled || disabledFields.confidenceLevel"
					:is-valid="!errors.confidenceLevel"
					:validation-error="errorFor( `confidenceLevel` )"
					:value="confidenceLevel"
					:hint="$t( `common-gokr.components.NewKeyResultForm.confidenceLevelFieldHint` )"
					:label="$t( `common-gokr.components.NewKeyResultForm.confidenceLevelFieldLabel` )"
					name="confidenceLevel"
					@change="onConfidenceLevelChanged"
				/>
			</div>

			<div>
				<LabeledDropDown
					:disabled="disabled || disabledFields.type"
					:is-valid="!errors.type"
					:options="options.type"
					:validation-error="errorFor( `type` )"
					:value="type"
					:hint="$t( `common-gokr.components.NewKeyResultForm.typeFieldHint` )"
					:label="$t( `common-gokr.components.NewKeyResultForm.typeFieldLabel` )"
					name="type"
					@change="onTypeChanged"
				/>
			</div>

			<div v-if="isBinary">
				<LabeledDropDown
					:disabled="disabled || disabledFields.currentValue"
					:is-valid="!errors.currentValue"
					:options="options.binaryChoice"
					:validation-error="errorFor( `currentValue` )"
					:value="currentValue"
					:label="$t( `common-gokr.components.NewKeyResultForm.binaryFieldLabel` )"
					name="currentValue"
					@change="onCurrentValueChanged"
				/>
			</div>

			<div v-if="isCurrency" class="key-result-form__split-row">
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.currentValue"
						:focus="isFocused( `currentValue` )"
						:is-valid="!errors.currentValue"
						:left-icon="[ `far`, `dollar-sign`, ]"
						:selection="focusedSelectionFor( `currentValue` )"
						:validation-error="errorFor( `currentValue` )"
						:value="currentValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.currentValueFieldLabel` )"
						name="currentValue"
						@input="onCurrentValueInput"
						@click="onCurrentValueFocus"
					/>
				</div>
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.targetValue"
						:focus="isFocused( `targetValue` )"
						:is-valid="!errors.targetValue"
						:left-icon="[ `far`, `dollar-sign`, ]"
						:selection="focusedSelectionFor( `targetValue` )"
						:validation-error="errorFor( `targetValue` )"
						:value="targetValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.targetValue` )"
						name="targetValue"
						@input="onTargetValueInput"
						@click="onTargetValueFocus"
					/>
				</div>
			</div>

			<div v-if="isNumeric" class="key-result-form__split-row">
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.currentValue"
						:focus="isFocused( `currentValue` )"
						:is-valid="!errors.currentValue"
						:selection="focusedSelectionFor( `currentValue` )"
						:validation-error="errorFor( `currentValue` )"
						:value="currentValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.currentValueFieldLabel` )"
						name="currentValue"
						@input="onCurrentValueInput"
						@click="onCurrentValueFocus"
					/>
				</div>
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.targetValue"
						:focus="isFocused( `targetValue` )"
						:is-valid="!errors.targetValue"
						:selection="focusedSelectionFor( `targetValue` )"
						:validation-error="errorFor( `targetValue` )"
						:value="targetValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.targetValueFieldLabel` )"
						name="targetValue"
						@input="onTargetValueInput"
						@click="onTargetValueFocus"
					/>
				</div>
			</div>

			<div v-if="isPercentage" class="key-result-form__split-row">
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.currentValue"
						:focus="isFocused( `currentValue` )"
						:is-valid="!errors.currentValue"
						:right-icon="[ `far`, `percent`, ]"
						:selection="focusedSelectionFor( `currentValue` )"
						:validation-error="errorFor( `currentValue` )"
						:value="currentValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.currentValueFieldLabel` )"
						name="currentValue"
						@input="onCurrentValueInput"
						@click="onCurrentValueFocus"
					/>
				</div>
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="disabled || disabledFields.targetValue"
						:focus="isFocused( `targetValue` )"
						:is-valid="!errors.targetValue"
						:right-icon="[ `far`, `percent`, ]"
						:selection="focusedSelectionFor( `targetValue` )"
						:validation-error="errorFor( `targetValue` )"
						:value="targetValue"
						:label="$t( `common-gokr.components.NewKeyResultForm.targetValueFieldLabel` )"
						name="targetValue"
						@input="onTargetValueInput"
						@click="onTargetValueFocus"
					/>
				</div>
			</div>

			<div class="key-result-form__split-row">
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="true"
						:value="startDate"
						:label="$t( `common-gokr.components.NewKeyResultForm.startDateFieldLabel` )"
						name="startDate"
					/>
				</div>
				<div>
					<LabeledInputField
						:blur-is-input="false"
						:disabled="true"
						:value="endDate"
						:label="$t( `common-gokr.components.NewKeyResultForm.endDateFieldLabel` )"
						name="endDate"
					/>
				</div>
			</div>

			<div>
				<LabeledUserDropDown
					:disabled="disabled || disabledFields.ownerId"
					:is-valid="!errors.ownerId"
					:user-list="possibleKeyResultOwners"
					:validation-error="errorFor( `ownerId` )"
					:value="ownerId"
					:hint="$t( `common-gokr.components.NewKeyResultForm.ownerIdFieldHint` )"
					:label="$t( `common-gokr.components.NewKeyResultForm.ownerIdFieldLabel` )"
					name="ownerId"
					@change="onOwnerIdChanged"
				/>
			</div>

			<div>
				<LabeledInputField
					:blur-is-input="false"
					:disabled="true"
					:value="parentGoalName"
					:label="$t( `common-gokr.components.NewKeyResultForm.parentGoalFieldLabel` )"
					name="parentGoalName"
				/>
			</div>
		</main>
	</form>
</template>

<script>
import {
	FormMixin,
	LabeledInputField,
	LabeledConfidenceLevelDropDown,
	LabeledDropDown,
	LabeledUserDropDown
} from "@teaming/common-ui";
import { KeyResultBinaryChoice, KeyResultType } from "@teaming/common-enums";

/** @deprecated - KRs are now created AFTER a goal is created */
export default {
	name: "NewKeyResultForm",
	mixins: [ FormMixin, ],
	emits: [ "discard", ],
	components: {
		LabeledInputField,
		LabeledConfidenceLevelDropDown,
		LabeledDropDown,
		LabeledUserDropDown,
	},
	props: {
		disabledFields: {
			type: Object,
			default: () => ( {
				name: false,
				confidenceLevel: false,
				type: false,
				currentValue: false,
				targetValue: false,
				ownerId: false,
			} ),
		},
		name: {
			type: String,
			required: true,
		},
		confidenceLevel: {
			type: String,
			required: true,
		},
		type: {
			type: String,
			required: true,
			validator( value ) {
				return KeyResultType.includes( value );
			},
		},
		startValue: {
			type: String,
			required: true,
		},
		currentValue: {
			type: String,
			required: true,
		},
		targetValue: {
			type: String,
			required: true,
		},
		ownerId: {
			type: String,
			required: true,
		},
		possibleKeyResultOwners: {
			type: Array,
			required: true,
		},
		errors: {
			type: Object,
			required: true,
		},
		number: {
			type: Number,
			required: true,
		},
		startDate: {
			type: String,
			required: true,
		},
		endDate: {
			type: String,
			required: true,
		},
		parentGoalName: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			nameChange: null,
		};
	},
	computed: {
		/** see FormMixin **/
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			const {
				formId,
				name,
				confidenceLevel,
				type,
				startValue,
				currentValue,
				targetValue,
				ownerId,
			} = this;

			return {
				formId,
				name,
				confidenceLevel,
				type,
				startValue,
				currentValue,
				targetValue,
				ownerId,
			};
		},
		options() {
			return {
				type: [
					{
						text: "Binary",
						value: KeyResultType.BINARY,
					},
					{
						text: "Percentage",
						value: KeyResultType.PERCENTAGE,
					},
					{
						text: "Currency",
						value: KeyResultType.CURRENCY,
					},
					{
						text: "Numeric",
						value: KeyResultType.NUMERIC,
					},
				],
				binaryChoice: [
					{
						text: "Incomplete",
						value: KeyResultBinaryChoice.INCOMPLETE,
					},
					{
						text: "Complete",
						value: KeyResultBinaryChoice.COMPLETE,
					},
				],
			};
		},
		isBinary() {
			return this.type === KeyResultType.BINARY;
		},
		isPercentage() {
			return this.type === KeyResultType.PERCENTAGE;
		},
		isCurrency() {
			return this.type === KeyResultType.CURRENCY;
		},
		isNumeric() {
			return this.type === KeyResultType.NUMERIC;
		},
	},
	methods: {
		onDiscardClicked() {
			this.$emit( "discard" );
		},
		onNameInput( e ) {
			clearTimeout( this.nameChange );
			this.nameChange = setTimeout( () => {
				const name = e.target.value;
				this.raiseFormChange( {
					name,
				} );
				const { selectionStart, } = e.target;
				this.raiseFocusChange( "name", selectionStart );
				// eslint-disable-next-line no-magic-numbers
			}, 50 );
		},
		onNameFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "name", selectionStart );
		},
		onConfidenceLevelChanged( option ) {
			const { value: confidenceLevel, } = option;
			this.raiseFormChange( {
				confidenceLevel,
			} );
			this.raiseFocusChange( "confidenceLevel" );
		},
		onTypeChanged( option ) {
			const { value: type, } = option;
			this.raiseFormChange( {
				type,
			} );
			this.raiseFocusChange( "type" );
		},
		onOwnerIdChanged( member ) {
			const { personId: ownerId, } = member;
			this.raiseFormChange( {
				ownerId,
			} );
			this.raiseFocusChange( "ownerId" );
		},
		onCurrentValueChanged( option ) {
			const { value: currentValue, } = option;
			this.raiseFormChange( {
				currentValue,
			} );
			this.raiseFocusChange( "currentValue" );
		},
		onCurrentValueInput( e ) {
			const { value: currentValue, } = e.target;
			this.raiseFormChange( {
				currentValue,
			} );
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "currentValue", selectionStart );
		},
		onCurrentValueFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "currentValue", selectionStart );
		},
		onTargetValueInput( e ) {
			const { value: targetValue, } = e.target;
			this.raiseFormChange( {
				targetValue,
			} );
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "targetValue", selectionStart );
		},
		onTargetValueFocus( e ) {
			const { selectionStart, } = e.target;
			this.raiseFocusChange( "targetValue", selectionStart );
		},
	},
};
</script>

<style lang="scss" scoped>
$key-result-form-title-color: $color1-700;
$key-result-form-border-color: $color1-200;
$key-result-form-background-color: $color1-050;

.key-result-form {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;

	background-color: $key-result-form-background-color;
	border: 0.0625rem solid $key-result-form-border-color;
	border-radius: 0.5rem;

	padding: 1rem;

	header {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

	main {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		gap: 1rem;
	}
}

.key-result-form__title {
	@include font-and-line-for(1);
	color: $key-result-form-title-color;
	font-weight: 600;
	text-align: left;
}

.key-result-form__discard {
	@include font-and-line-for(1);
	cursor: pointer;
}

.key-result-form__split-row {
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
	gap: 1rem;

	> div {
		flex: 1;
	}
}
</style>
